import Anthropic from '@anthropic-ai/sdk';

// Message interface for AI service
interface AiMessage {
  role: 'user' | 'assistant';
  content: string;
}

// Message interface for the Anthropic API
interface ClaudeMessage {
  role: 'user' | 'assistant';
  content: string;
}

// Options for making a Claude API request
interface ClaudeRequestOptions {
  model?: string;
  temperature?: number;
  maxTokens?: number;
  apiKey?: string;
  system?: string;
  // Thread-specific settings
  enableThinking?: boolean;
  enableWebSearch?: boolean;
}

// Token usage metrics
interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
}

// Web search source information
interface WebSearchSource {
  url: string;
  title?: string;
  snippet?: string;
}

// Streaming callback for draft updates
interface StreamCallback {
  onStart?: () => void;
  onThinking?: (thinkingText: string) => void;
  onContent?: (contentText: string) => void;
  onWebSearchSources?: (sources: WebSearchSource[]) => void;
  onComplete?: (finalContent: string, finalThinking: string, usage: TokenUsage, webSearchSources?: WebSearchSource[]) => void;
  onError?: (error: Error) => void;
}

export class AIService {
  /**
   * Sends a message to Claude API with streaming support for draft updates
   */
  static async sendMessageStream(
    messages: AiMessage[],
    callbacks: StreamCallback,
    options: ClaudeRequestOptions = {}
  ): Promise<void> {
    // Default options - using Claude 4 with thinking support
    const defaultModel = 'claude-4-sonnet-20250514';
    const defaultMaxTokens = 8192;
    const defaultTemp = 1;
    
    // Get API key from options
    let apiKey = options.apiKey;
    
    if (!apiKey) {
      throw new Error('API key must be provided from server via secure endpoint');
    }
    
    // Create Anthropic client
    // Using dangerouslyAllowBrowser because we maintain end-to-end encryption
    // and users provide their own API keys (not shared company keys)
    const anthropic = new Anthropic({
      apiKey: apiKey,
      dangerouslyAllowBrowser: true
    });
    
    // Convert our message format to Claude format
    const claudeMessages: ClaudeMessage[] = messages.map(msg => ({
      role: msg.role,
      content: msg.content
    }));
    
    try {
      callbacks.onStart?.();
      
      // Make the streaming API call
      const stream = await anthropic.messages.create({
        model: options.model || defaultModel,
        max_tokens: options.maxTokens || defaultMaxTokens,
        temperature: options.temperature || defaultTemp,
        messages: claudeMessages,
        stream: true,
        ...(options.enableThinking && {
          thinking: {
            type: "enabled",
            budget_tokens: 5000
          }
        }),
        ...(options.enableWebSearch && {
          tools: [
            {
              type: "web_search_20250305",
              name: "web_search"
            }
          ]
        }),
        ...(options.system && { system: options.system }),
      });
      
      let fullContent = '';
      let fullThinking = '';
      let usage: TokenUsage = { inputTokens: 0, outputTokens: 0 };
      let webSearchSources: WebSearchSource[] = [];
      
      // Process the stream
      for await (const chunk of stream) {
        if (chunk.type === 'content_block_delta') {
          if (chunk.delta.type === 'text_delta') {
            // Regular content
            const chunkText = chunk.delta.text;
            fullContent += chunkText;
            callbacks.onContent?.(chunkText);
          } else if (chunk.delta.type === 'thinking_delta') {
            // Thinking content (Claude 4 reasoning)
            const thinkingText = chunk.delta.thinking;
            fullThinking += thinkingText;
            callbacks.onThinking?.(thinkingText);
          }
        } else if (chunk.type === 'content_block_start') {
          // Check if this is a web search tool result
          if (chunk.content_block?.type === 'web_search_tool_result') {
            const toolResult = chunk.content_block as any;
            if (toolResult.content && Array.isArray(toolResult.content)) {
              const newSources: WebSearchSource[] = [];
              for (const result of toolResult.content) {
                if (result.type === 'web_search_result' && result.url) {
                  const source: WebSearchSource = {
                    url: result.url,
                    title: result.title || new URL(result.url).hostname,
                    snippet: undefined // Not available in this format
                  };
                  newSources.push(source);
                }
              }
              webSearchSources.push(...newSources);
              if (newSources.length > 0) {
                callbacks.onWebSearchSources?.(newSources);
              }
            }
          }
        } else if (chunk.type === 'content_block_stop') {
          // Block completed
        } else if (chunk.type === 'message_delta' && chunk.usage) {
          usage.outputTokens = chunk.usage.output_tokens || 0;
        } else if (chunk.type === 'message_start' && chunk.message.usage) {
          usage.inputTokens = chunk.message.usage.input_tokens || 0;
        }
      }
      
      callbacks.onComplete?.(fullContent, fullThinking, usage, webSearchSources);
      
    } catch (error) {
      callbacks.onError?.(error as Error);
    }
  }

  
}